// A6 ML moderatsiya — data layer (real backend, `mod_ml_models` reyestri +
// mod_decisions / mod_decision_errors / mod_hashes ustidan hisoblanadigan
// ekranlar). REAL: A6.23 model-registry, A6.25 threshold, A6.27 perceptual-hash
// (CRUD) + A6.24 sifat, A6.38 inter-rater, A6.18 o'xshash holatlar (compute).
import { api } from '@/lib/api'

/* ── qator turlari ─────────────────────────────────────────────────── */

/** A6.23 / A6.25 — ML model konfiguratsiyasi. */
export interface MlModelRow {
  id: number
  name: string
  version: string
  kind: string
  status: string
  threshold: number
  notes: string
  created_by: string
  updated_at: string
}

/** A6.27 — perceptual (pHash) hash yozuvi. */
export interface PhashRow {
  id: number
  hash: string
  category: string
  source: string
  added_by: string
  created_at: string
}

/** A6.24 — model sifati (confusion matrix + hosila metrikalar). */
export interface ModelQuality {
  tp: number
  fp: number
  fn: number
  tn: number
  precision: number
  recall: number
  f1: number
  accuracy: number
  total_decisions: number
  total_errors: number
  by_action: { action: string; total: number; overturned: number; precision: number }[]
}

/** A6.38 — inter-rater kelishuv (ko'p-baholovchi qaror statistikasi). */
export interface InterRaterData {
  multi_rated: number
  agreed: number
  disputed: number
  agreement_pct: number
  rows: {
    id: string
    target_type: string
    target_id: number
    decisions: number
    raters: number
    agree: boolean
    actions: string
  }[]
}

/** A6.18 — o'xshash holatlar klasteri. */
export interface SimilarCluster {
  id: string
  target_type: string
  action: string
  reason: string
  cases: number
  targets: number
  first_at: string
  last_at: string
}

/* ── mutatsiyalar (A6.23 / A6.25 / A6.27) ──────────────────────────── */

export const createMlModel = (b: {
  name: string
  version: string
  kind: string
  status: string
  threshold: number
  notes?: string
}) => api.post<{ id: number }>('/admin/moderation/ml-models', b)

export const updateMlModel = (
  id: number,
  b: { status?: string; threshold?: number; version?: string; kind?: string; notes?: string },
) => api.put(`/admin/moderation/ml-models/${id}`, b)

export const deleteMlModel = (id: number) => api.del(`/admin/moderation/ml-models/${id}`)

export const createPhash = (b: { hash: string; category: string; source: string }) =>
  api.post<{ id: number }>('/admin/moderation/phash', b)

export const deletePhash = (id: number) => api.del(`/admin/moderation/phash/${id}`)

/* ── compute fetcherlar (A6.24 / A6.38 / A6.18) — fail-soft ─────────── */

export async function fetchMlModels(signal: AbortSignal): Promise<MlModelRow[]> {
  try {
    const d = await api.get<{ models: MlModelRow[] }>('/admin/moderation/ml-models', { signal })
    return d?.models ?? []
  } catch {
    return []
  }
}

export async function fetchModelQuality(signal: AbortSignal): Promise<ModelQuality | null> {
  try {
    return await api.get<ModelQuality>('/admin/moderation/model-quality', { signal })
  } catch {
    return null
  }
}

export async function fetchInterRater(signal: AbortSignal): Promise<InterRaterData | null> {
  try {
    return await api.get<InterRaterData>('/admin/moderation/inter-rater', { signal })
  } catch {
    return null
  }
}

export async function fetchSimilarClusters(signal: AbortSignal): Promise<SimilarCluster[]> {
  try {
    const d = await api.get<{ clusters: SimilarCluster[] }>('/admin/moderation/similar', { signal })
    return d?.clusters ?? []
  } catch {
    return []
  }
}

/* ── modul-lokal satrlar ───────────────────────────────────────────── */

export const ML_STR = {
  empty: "Ma'lumot yo'q",
  failed: 'Amal bajarilmadi',
  add: "Qo'shish",
  delete: "O'chirish",
  save: 'Saqlash',
  saved: 'Saqlandi',
  refresh: 'Yangilash',
  category: 'Toifa',
  created: 'Yaratilgan',
  updatedAt: 'Yangilangan',
  status: 'Holat',
  action: 'Amal',
  source: 'Manba',
  addedBy: "Kim qo'shdi",
  confirmDelete: "Yozuvni o'chirasizmi?",
  // A6.23
  models: 'ML modellar reyestri',
  modelsDesc: 'Model konfiguratsiyasi — nom / versiya / holat / chegara (CRUD)',
  name: 'Nomi',
  version: 'Versiya',
  kind: 'Turi',
  threshold: 'Chegara',
  notes: 'Izoh',
  namePrompt: 'Model nomi:',
  versionPrompt: 'Versiya (masalan v1):',
  kindPrompt: 'Turi (toxicity|nsfw|spam|scam|phash|other):',
  statusPrompt: 'Holat (draft|staging|active|disabled):',
  thresholdPrompt: 'Chegara (0.0 – 1.0):',
  needName: 'Model nomini kiriting',
  modelAdded: "Model qo'shildi",
  modelDeleted: "Model o'chirildi",
  // A6.25
  thresholds: 'Threshold sozlash',
  thresholdsDesc: "Har model bo'yicha skoring chegarasi va holatini sozlash",
  noModels: "Avval A6.23 (ML modellar reyestri) da model qo'shing",
  // A6.27
  phash: 'Perceptual hash reyestri',
  phashDesc: "Rasm/video pHash yozuvlari (mod_hashes, hash_type=phash) — CRUD",
  hash: 'pHash',
  hashPrompt: 'pHash qiymati:',
  sourcePrompt: 'Manba (ixtiyoriy):',
  phashAdded: "pHash qo'shildi",
  phashDeleted: "pHash o'chirildi",
  needHash: 'pHash qiymatini kiriting',
  // A6.24
  quality: 'Model sifati (precision/recall)',
  qualityDesc: 'Qarorlar (mod_decisions) va bekor qilingan qarorlar asosida real confusion-matrix',
  precision: 'Precision',
  recall: 'Recall',
  f1: 'F1',
  accuracy: 'Aniqlik',
  confusion: 'Confusion matrix',
  tp: 'TP (to’g’ri ijobiy)',
  fp: 'FP (noto’g’ri ijobiy)',
  fn: 'FN (o’tkazib yuborilgan)',
  tn: 'TN (to’g’ri salbiy)',
  byAction: 'Amal bo’yicha aniqlik',
  total: 'Jami',
  overturned: 'Bekor qilingan',
  totalDecisions: 'Jami qarorlar',
  totalErrors: 'Xato qarorlar',
  noData: 'Hisoblash uchun qaror mavjud emas',
  // A6.38
  interRater: 'Kelishuv darajasi (inter-rater)',
  interRaterDesc: "Bir obyektni bir nechta admin baholaganda kelishuv foizi",
  agreement: 'Kelishuv',
  multiRated: "Ko'p-baholangan",
  agreed: 'Kelishilgan',
  disputed: 'Nizoli',
  target: 'Obyekt',
  raters: 'Baholovchilar',
  decisions: 'Qarorlar',
  actions: 'Amallar',
  agree: 'Kelishuv',
  noMultiRater: "Bir obyektni 2+ admin baholagan holat hali yo'q",
  // A6.18
  similar: "O'xshash holatlar (klaster)",
  similarDesc: "Qarorlar obyekt-turi + amal + sabab bo'yicha klasterlangan (takrorlanuvchi holatlar)",
  reason: 'Sabab',
  cases: 'Holatlar',
  targets: 'Obyektlar',
  firstAt: 'Birinchi',
  lastAt: 'Oxirgi',
  noClusters: 'Takrorlanuvchi holat klasteri topilmadi',
} as const
